﻿import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { userTypeIds } from 'config/config';

import Loader from 'components/Loader';
import ErrorBoundary from 'components/ErrorBoundary';

// pages
import requirePersonalInformation from 'components/requirePersonalInformation';
import requireAuth from 'components/requireAuth';
import requireMaintenancePlan from 'components/requireMaintenancePlan';
import App from 'pages/App/App';
import ClientDetails from 'pages/ClientDetails';
import Clients from 'pages/Clients';
import CreateTreatmentPlan from 'pages/CreateTreatmentPlan';
import CreateRegimen from 'pages/CreateRegimen';
import Dashboard from 'pages/Dashboard';
import Documents from 'pages/Documents';
import ResetPassword from 'pages/ResetPassword';
import ConversationPage from 'pages/Conversation';
import ConversationHistoryPage from 'pages/ConversationHistory';
import Login from 'pages/Login';
import TreatmentPlanPage from 'pages/TreatmentPlanPage';
import SurveysOverview from 'pages/SurveysOverview';
import AddPersonalInformation from 'pages/AddPersonalInformation';
import AddTimeZone from 'pages/AddTimeZone';
import SurveyPage from 'pages/Survey';
import SurveyRemotePage from 'pages/SurveyRemote';

// Lazy loaded pages
const EndUserLicenseAgreement = React.lazy(() =>
  import('pages/EndUserLicenseAgreement'),
);
const MaintenancePlan = React.lazy(() => import('pages/MaintenancePlan'));
const NewSurvey = React.lazy(() => import('pages/NewSurvey'));

// Tasks pages
import ReviewTreatmentPlan from './containers/TaskWidget/ReviewTreatmentPlanWidget/ReviewTreatmentPlan';

import SurveyOverview from 'pages/SurveyOverview';
import ViewRegimen from 'pages/ViewRegimen';
import ClientOrderProducts from 'pages/ClientOrderProducts';
import ProgramConsent from 'pages/ProgramConsent';
import CreateAccount from 'pages/CreateAccount/CreateAccount';

// Pre-wrap components with HOCs outside render to prevent component unmounting/remounting
const ProtectedDashboard = requireAuth(
  requirePersonalInformation(requireMaintenancePlan(Dashboard)),
);
const ProtectedClients = requireAuth(Clients);
const ProtectedConversationHistory = requireAuth(ConversationHistoryPage);
const ProtectedClientDetails = requireAuth(ClientDetails);
const MaintenanceSurveyPage = requireMaintenancePlan(SurveyPage);
const MaintenanceSurveyRemotePage = requireMaintenancePlan(SurveyRemotePage);
const ProtectedDocuments = requireAuth(Documents);
const ProtectedConversationPage = requireAuth(requireMaintenancePlan(ConversationPage));
const ProtectedSurveysOverview = requireAuth(SurveysOverview);
const ProtectedNewSurvey = requireAuth(NewSurvey);
const ProtectedSurveyOverview = requireAuth(SurveyOverview, [
  userTypeIds.coach,
  userTypeIds.practice_manager,
]);
const ProtectedClientOrderProducts = requireAuth(ClientOrderProducts);
// Sentry needs to see route changes to name navigation transactions. With the
// declarative <Routes> API this wrapper is how it hooks in; without it the integration
// records page loads only, which is what the app did for the whole upgrade.
const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

const ProtectedAddPersonalInfo = requireAuth(AddPersonalInformation);
const ProtectedAddTimeZone = requireAuth(AddTimeZone);
const ProtectedCreateTreatmentPlan = requireAuth(CreateTreatmentPlan);
const ProtectedTreatmentPlanPage = requireAuth(requireMaintenancePlan(TreatmentPlanPage));
const ProtectedCreateRegimen = requireAuth(CreateRegimen);
const ProtectedViewRegimen = requireAuth(requireMaintenancePlan(ViewRegimen));
const ProtectedProgramConsent = requireAuth(ProgramConsent);
const ProtectedMaintenancePlan = requireAuth(MaintenancePlan);
const ProtectedReviewTreatmentPlan = requireAuth(ReviewTreatmentPlan);

// React 19 unmounts the whole tree on an uncaught render error, which is why every
// crash during this upgrade showed up as a blank page rather than a broken region: a
// null in a task list, a modal reading profile.full_name, the check-in's drag-and-drop
// library. This boundary means a page can fail without taking the application with it.
//
// It sits inside <App>, so the navbar survives and the user can still navigate away,
// and it is keyed on the pathname so leaving a broken page clears the error instead of
// carrying it to the next one. It reports to Sentry through the shared ErrorBoundary,
// so contained crashes stay visible to us rather than being quietly swallowed.
const RoutedPageBoundary = ({ children }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

RoutedPageBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

export default () => (
  <App>
    <React.Suspense fallback={<Loader type="fixed" loading size="large" />}>
      <RoutedPageBoundary>
        <SentryRoutes>
          {/* App */}
          <Route path="/login" element={<Login />} />
          <Route path="/account-create/:userId" element={<CreateAccount />} />
          <Route path="/password-reset/:userId" element={<ResetPassword />} />

          {/* Dashboard */}
          <Route path="/" element={<ProtectedDashboard />} />

          {/* Clients */}
          <Route path="/clients" element={<ProtectedClients />} />
          <Route
            path="/client/:clientId/messages"
            element={<ProtectedConversationHistory />}
          />
          <Route
            path="/client-details/:id"
            element={<ProtectedClientDetails />}
          />
          <Route
            path="/client-details/:clientId/surveys/:surveyId/:responseId"
            element={<MaintenanceSurveyPage />}
          />
          <Route
            path="/client-details/:clientId/surveys/hq"
            element={<MaintenanceSurveyRemotePage />}
          />

          {/* Documents */}
          <Route path="/documents/*" element={<ProtectedDocuments />} />

          {/* EULA */}
          <Route path="/eula" element={<EndUserLicenseAgreement />} />

          {/* Chat */}
          <Route
            path="/messages/:threadId"
            element={<ProtectedConversationPage />}
          />

          {/* HQ & Check-In */}
          <Route path="/surveys" element={<ProtectedSurveysOverview />} />
          <Route path="/surveys/:surveyId/:pageId?" element={<ProtectedNewSurvey />} />
          <Route path="/checkin/:surveyId/:pageId?" element={<ProtectedNewSurvey />} />
          <Route
            path="/survey-overview/:surveyId/:pageId?"
            element={<ProtectedSurveyOverview />}
          />
          <Route
            path="/order-products/:clientId?/:programId?/:stageId?"
            element={<ProtectedClientOrderProducts />}
          />
          <Route
            path="/add-personal-information"
            element={<ProtectedAddPersonalInfo />}
          />
          <Route path="/add-time-zone" element={<ProtectedAddTimeZone />} />

          {/* Treatment Plan */}
          <Route
            path="/create-treatment-plan/:clientId"
            element={<ProtectedCreateTreatmentPlan />}
          />
          <Route
            path="/programs/acne/treatment-plans/:treatmentPlanId"
            element={<ProtectedTreatmentPlanPage />}
          />

          {/* Regimen */}
          <Route
            path="/create-regimen/:clientId?/:programId?"
            element={<ProtectedCreateRegimen />}
          />
          <Route
            path="/view-regimen/:stageId"
            element={<ProtectedViewRegimen />}
          />
          <Route
            path="/regimen-consent/:programId"
            element={<ProtectedProgramConsent />}
          />

          {/* MaintenancePlan */}
          <Route
            path="/maintenance-plans"
            element={<ProtectedMaintenancePlan />}
          />

          {/* New abstraction layer for containers/TaskWidget/XXXTask */}
          <Route
            path="/review-treatment-plan/:treatmentPlanId/:taskId"
            element={<ProtectedReviewTreatmentPlan />}
          />
        </SentryRoutes>
      </RoutedPageBoundary>
    </React.Suspense>
  </App>
);
